import { ERROR_CODES, type MeResponse, meResponseSchema } from '@diario/common';
import { queryOptions, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { ApiError, apiGet } from '../../../lib/http';

/**
 * Opciones de la query de sesión (`GET /me`), exportadas aparte del hook porque la oleada 3
 * las necesita para `ensureQueryData` en el `beforeLoad` del router (protección de rutas), sin
 * duplicar aquí la definición de la petición.
 *
 * La sesión vive en TanStack Query, no en Zustand: duplicarla en un store es justo el
 * antipatrón que la separación cliente/servidor de este proyecto existe para evitar.
 *
 * `retry` distingue el criterio CA-02a.5 («un fallo de `GET /me` no cierra la sesión; se
 * reintenta»): `ERR-006` es la respuesta esperada cuando de verdad no hay sesión — reintentarla
 * no cambiaría el resultado, así que no se reintenta. Cualquier otro fallo (`ERR-000` de red
 * incluido) SÍ se reintenta, porque no permite concluir que no hay sesión. `retryDelay` se fija
 * corto (100 ms) a propósito: es una comprobación ligera que no debería dejar a la persona
 * mirando una pantalla de carga con el backoff largo por defecto de TanStack Query.
 */
export const sesionQueryOptions = queryOptions({
	queryFn: () => apiGet('/me', meResponseSchema),
	queryKey: ['sesion'] as const,
	retry: (failureCount: number, error: Error) => {
		if (error instanceof ApiError && error.code === ERROR_CODES.unauthorized) {
			return false;
		}
		return failureCount < 1;
	},
	retryDelay: 100,
});

export interface ResultadoUseSesion {
	/** `true` mientras la primera comprobación sigue en curso (incluidos los reintentos). */
	cargando: boolean;
	/**
	 * `true` únicamente cuando el backend ha confirmado `ERR-006`: no hay sesión. Un fallo
	 * cualquiera que no sea ese (red caída, `ERR-000`, un 500…) nunca activa este booleano —
	 * para eso está `noSeConfirmo`, distinto y no intercambiable (CA-02a.5).
	 */
	noHaySesion: boolean;
	/**
	 * `true` cuando la comprobación ha terminado en un fallo que NO es «no hay sesión»: no se
	 * puede concluir nada todavía y, en particular, no se debe mandar a la persona al acceso.
	 */
	noSeConfirmo: boolean;
	/** La query subyacente de TanStack Query, para quien necesite más detalle (`refetch`…). */
	query: UseQueryResult<MeResponse>;
	/** La sesión, cuando la comprobación ha terminado con éxito. */
	sesion: MeResponse | undefined;
}

/**
 * Estado de sesión de la persona (`GET /me`). Expone, aparte de los datos, la distinción
 * crítica del criterio CA-02a.5: «no hay sesión» (se puede mandar al acceso) frente a «no se
 * pudo comprobar» (fallo de red u otro fallo transitorio — nunca se concluye que no hay sesión).
 */
export const useSesion = (): ResultadoUseSesion => {
	const query = useQuery(sesionQueryOptions);
	const noHaySesion = query.isError && query.error instanceof ApiError && query.error.code === ERROR_CODES.unauthorized;

	return {
		cargando: query.isPending,
		noHaySesion,
		noSeConfirmo: query.isError && !noHaySesion,
		query,
		sesion: query.data,
	};
};
